import React, { useCallback, useState, useEffect, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import SegmentService from 'redux/config/services/SegmentService'
import { AppState } from 'redux/config/store'
import { employeeActions } from 'redux/employee/employeeSlice'
import {
  emptyRosterFilters,
  rosterFiltersFromQuery,
  RosterFilterSelection,
  RosterTab,
  ROSTER_PAGE_SIZE,
} from 'redux/employee/rosterQuery'
import { Segment } from 'redux/employer/employerTypes'
import { ServerPagination } from 'core-system/Table/Table'
import PageHeader from '../components/PageHeader'
import PageBodyContainer from '../components/PageBodyContainer'
import InviteUsersModal from '../components/InviteUsersModal'
import UploadDataModal from '../components/UploadDataModal'
import CommuterTableOperations from './CommuterTableOperations'
import CommutersTable from './CommutersTable'
import styled from 'styled-components'
import palette from 'core-system/Themes/palette'
import {
  TableManipulationTopContainer,
  TableManipulationItem,
} from 'core-system/Table'
import MyCommutersArchived from './MyCommutersArchived'
import MyCommutersDeclined from './MyCommutersDeclined'
import FlexContainer from 'core-system/FlexContainer'
import Text from 'core-system/Text'
import MyCommutersPending from './MyCommutersPending'
import MyCommutersBlocked from './MyCommutersBlocked'

const HeaderSpan = styled(Text)`
  margin-left: 0.5rem;
`

const TableContainer = styled(TableManipulationTopContainer)`
  margin-bottom: 0 !important;
  border: 0;
  padding: 0;

  @media (max-width: ${({ theme }) => theme.screenSize.sm}) {
    border: 1px solid ${({ theme }) => theme.palette.grey.grey300};
    padding: 1rem;
  }
`

const TopContainer = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 1.5rem;

  @media (max-width: ${({ theme }) => theme.screenSize.sm}) {
    flex-direction: column;
    align-items: flex-start;
    gap: 1rem;
  }
`

const getHeaderColor = (active: boolean, disabled: boolean) => {
  if (active) {
    return palette.white
  } else if (disabled) {
    return palette.text.disabled
  } else {
    return palette.secondary.purple700
  }
}

const HeaderItem = ({
  text,
  isDisabled,
  isActive,
  numCommuters,
  onClick,
}: {
  text: string
  isDisabled: boolean
  isActive: boolean
  numCommuters: number
  onClick: () => void
}) => {
  return (
    <TableManipulationItem
      onClick={onClick}
      active={isActive}
      disabled={isDisabled}
    >
      {numCommuters}
      <HeaderSpan
        variant='action4'
        color={getHeaderColor(isActive, isDisabled)}
      >
        {text}
      </HeaderSpan>
    </TableManipulationItem>
  )
}

interface MyCommutersView {
  activeSegment: Segment
  activeTab: number
  setActiveTab: (tab: number) => void
  allEmployeeSegmentId: string
  tabs: { label: string; value: number }[]
}

const MyCommutersView = React.memo((props: MyCommutersView) => {
  const { activeSegment, activeTab, setActiveTab, allEmployeeSegmentId, tabs } =
    props

  const dispatch = useDispatch()
  const { rosterPages, rosterSummary, rosterLoading, loadedRosterQuery } =
    useSelector((state: AppState) => state.employee)
  const savedQuery =
    loadedRosterQuery?.segment === allEmployeeSegmentId ? loadedRosterQuery : null
  const [activeTable, setActiveTable] = useState<RosterTab>(savedQuery?.tab || 'all')
  const [page, setPage] = useState(savedQuery?.page || 1)
  const [pageSize, setPageSize] = useState(savedQuery?.pageSize || ROSTER_PAGE_SIZE)
  const [filters, setFilters] = useState<RosterFilterSelection>(
    savedQuery ? rosterFiltersFromQuery(savedQuery) : emptyRosterFilters()
  )
  const searchTimer = useRef<number>()
  const loadedRosterQueryRef = useRef(loadedRosterQuery)
  const rosterPagesRef = useRef(rosterPages)
  const rosterSummaryRef = useRef(rosterSummary)
  loadedRosterQueryRef.current = loadedRosterQuery
  rosterPagesRef.current = rosterPages
  rosterSummaryRef.current = rosterSummary

  const requestPage = useCallback(
    (
      tab: RosterTab,
      nextPage: number,
      nextPageSize: number,
      nextFilters: RosterFilterSelection
    ) => {
      dispatch(
        employeeActions.getRosterPage({
          tab,
          segment: allEmployeeSegmentId,
          page: nextPage,
          pageSize: nextPageSize,
          ...nextFilters,
        })
      )
    },
    [allEmployeeSegmentId, dispatch]
  )

  useEffect(() => {
    const saved = loadedRosterQueryRef.current
    const hasPage = Boolean(
      saved &&
        saved.segment === allEmployeeSegmentId &&
        rosterPagesRef.current[saved.tab]
    )
    if (!rosterSummaryRef.current) {
      dispatch(
        employeeActions.getRosterSummary({ segment: allEmployeeSegmentId })
      )
    }
    if (hasPage) {
      return
    }
    const freshFilters = emptyRosterFilters()
    setActiveTable('all')
    setPage(1)
    setPageSize(ROSTER_PAGE_SIZE)
    setFilters(freshFilters)
    requestPage('all', 1, ROSTER_PAGE_SIZE, freshFilters)
  }, [allEmployeeSegmentId, dispatch, requestPage])

  useEffect(() => {
    return () => window.clearTimeout(searchTimer.current)
  }, [])

  const { segmentsMap } = useSelector((state: AppState) => state.employer)
  const hrisStatus = useSelector((state: AppState) => ({
    providerName: state.employer.profile?.payrollProviderId,
    reauth: state.employer.profile?.finchReauth,
  }))
  const apiRosterSyncEnabled = useSelector((state: AppState) =>
    Boolean(state.employer.profile?.apiRosterSyncEnabled)
  )

  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false)
  const [uploadSegment, setUploadSegment] = useState(null)

  const [showInviteUsersModal, setShowInviteUsersModal] = useState(false)

  const toggleInviteUsersModal = React.useCallback(() => {
    setShowInviteUsersModal(!showInviteUsersModal)
    SegmentService.track('inviteUsers-modal-click', {
      action: showInviteUsersModal ? 'close' : 'open',
    })
  }, [showInviteUsersModal])

  const handleUploadModalOpen = useCallback(
    (segment?: string) => {
      if (segment) {
        setUploadSegment(segmentsMap[segment])
      }
      setIsUploadModalOpen(true)
    },
    [segmentsMap]
  )

  const handleHeaderItemClick = (table: RosterTab) => {
    if (table === activeTable) {
      return
    }
    const cleared = emptyRosterFilters()
    window.clearTimeout(searchTimer.current)
    setActiveTable(table)
    setFilters(cleared)
    setPage(1)
    requestPage(table, 1, pageSize, cleared)
  }

  const handleServerQueryChange = (nextFilters: RosterFilterSelection) => {
    const searchChanged = nextFilters.search !== filters.search
    setFilters(nextFilters)
    setPage(1)
    window.clearTimeout(searchTimer.current)
    const run = () => requestPage(activeTable, 1, pageSize, nextFilters)
    if (searchChanged) {
      searchTimer.current = window.setTimeout(run, 300)
    } else {
      run()
    }
  }

  const counts = rosterSummary?.counts
  const activePage = rosterPages[activeTable]
  const pageMatchesTab = loadedRosterQuery?.tab === activeTable
  const visibleRows = pageMatchesTab ? activePage?.results || [] : []
  const tableLoading = rosterLoading || !pageMatchesTab
  const serverPagination: ServerPagination = {
    count: pageMatchesTab ? activePage?.count || 0 : counts?.[activeTable] || 0,
    page: Math.max((pageMatchesTab ? activePage?.page || page : page) - 1, 0),
    rowsPerPage: pageSize,
    onPageChange: (nextPage) => {
      const apiPage = nextPage + 1
      setPage(apiPage)
      requestPage(activeTable, apiPage, pageSize, filters)
    },
    onRowsPerPageChange: (nextPageSize) => {
      setPageSize(nextPageSize)
      setPage(1)
      requestPage(activeTable, 1, nextPageSize, filters)
    },
  }

  return (
    <>
      <PageHeader
        inviteCommutersCSV={handleUploadModalOpen}
        hrisStatus={hrisStatus}
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        tabs={tabs}
        showAddCommutersButton={!apiRosterSyncEnabled}
        showInviteCommutersButton
        onInviteCommutersClick={toggleInviteUsersModal}
        inviteCommutersCSVDisabled={hrisStatus?.providerName ? true : false}
        inviteCommutersCSVTooltip={
          hrisStatus?.providerName
            ? 'You cannot import commuters via CSV while connected to a payroll provider through HRIS'
            : ''
        }
      />
      <InviteUsersModal
        segmentId={allEmployeeSegmentId}
        closeModal={toggleInviteUsersModal}
        open={showInviteUsersModal}
      />
      <PageBodyContainer>
        <TopContainer>
          <TableContainer>
            <HeaderItem
              text='All'
              isDisabled={!counts || counts.all === 0}
              isActive={activeTable === 'all'}
              numCommuters={counts?.all || 0}
              onClick={() => handleHeaderItemClick('all')}
            />
            <HeaderItem
              text='Pending'
              isDisabled={!counts || counts.pending === 0}
              isActive={activeTable === 'pending'}
              numCommuters={counts?.pending || 0}
              onClick={() => handleHeaderItemClick('pending')}
            />
            <HeaderItem
              text='Archived'
              isDisabled={!counts || counts.archived === 0}
              isActive={activeTable === 'archived'}
              numCommuters={counts?.archived || 0}
              onClick={() => handleHeaderItemClick('archived')}
            />
            <HeaderItem
              text='Blocked'
              isDisabled={!counts || counts.blocked === 0}
              isActive={activeTable === 'blocked'}
              numCommuters={counts?.blocked || 0}
              onClick={() => handleHeaderItemClick('blocked')}
            />
            <HeaderItem
              text='Declined'
              isDisabled={!counts || counts.declined === 0}
              isActive={activeTable === 'declined'}
              numCommuters={counts?.declined || 0}
              onClick={() => handleHeaderItemClick('declined')}
            />
          </TableContainer>
          <CommuterTableOperations
            key={activeTable}
            commuters={visibleRows}
            serverFilters={
              activeTable === 'all' ? rosterSummary?.filters : undefined
            }
            hideFilters={activeTable !== 'all'}
            initialQuery={filters}
            onServerQueryChange={handleServerQueryChange}
          />
        </TopContainer>
        {activeTable === 'all' && (
          <CommutersTable
            myCommuters={visibleRows}
            activeCommuters={visibleRows}
            activeSegment={activeSegment}
            showCommuterTableOperations={false}
            isEdit={false}
            isAdd={false}
            serverPagination={serverPagination}
            isLoading={tableLoading}
          />
        )}
        {activeTable === 'archived' && (
          <MyCommutersArchived
            archivedEmployees={visibleRows}
            activeSegment={allEmployeeSegmentId}
            serverPagination={serverPagination}
            isLoading={tableLoading}
          />
        )}
        {activeTable === 'blocked' && (
          <MyCommutersBlocked
            blockedEmployees={visibleRows}
            serverPagination={serverPagination}
            isLoading={tableLoading}
          />
        )}
        {activeTable === 'declined' && (
          <MyCommutersDeclined
            declinedEmployees={visibleRows}
            serverPagination={serverPagination}
            isLoading={tableLoading}
          />
        )}
        {activeTable === 'pending' && (
          <>
            <FlexContainer
              justifyContent='space-between'
              alignItems='center'
              marginBottom='1.5rem'
            >
              <Text variant='body1'>
                These commuters have signed up but aren’t part of any groups
                yet. Select and approve the commuters you authorize to be part
                of the platform. Once approved they will be part of{' '}
                <strong>{segmentsMap[allEmployeeSegmentId].name}</strong>. Any
                declined commuters will be removed.
              </Text>
            </FlexContainer>
            <MyCommutersPending
              pendingCommuters={visibleRows}
              activeSegment={allEmployeeSegmentId}
              serverPagination={serverPagination}
              isLoading={tableLoading}
            />
          </>
        )}
      </PageBodyContainer>
      <UploadDataModal
        open={isUploadModalOpen}
        closeModal={() => setIsUploadModalOpen(false)}
        segment={uploadSegment}
      />
    </>
  )
})

// Helps to identify component in React error logs
if (process.env.NODE_ENV !== 'production') {
  MyCommutersView.displayName = 'MyCommutersView'
}

export default MyCommutersView
